import { ListFilterBar } from '../../app/data-view.js';
import { Button, FormErrorSummary, StatusPill, SidePanel } from '@hue/design-system';
import { Check, UsersRound } from 'lucide-react';
import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  schedulingApi,
  getApiError,
  type Appointment,
  type AppointmentStatus,
} from '../../lib/api.js';
import { civilDateOf, formatDateTime } from '../../lib/time-zone.js';

/// Peças usadas pelas várias áreas da agenda — lista, calendário, recursos e bloqueios.

export function DialogActions({
  label,
  onCancel,
  loading,
  onSubmit,
}: {
  label: string;
  onCancel: () => void;
  loading?: boolean;
  onSubmit?: () => void;
}) {
  return (
    <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-surface-border pt-5">
      <FormErrorSummary className="basis-full sm:basis-auto sm:flex-1" />
      <Button type="button" variant="outline" onClick={onCancel}>
        Voltar
      </Button>
      <Button
        type={onSubmit ? 'button' : 'submit'}
        {...(loading === undefined ? {} : { loading })}
        {...(onSubmit ? { onClick: onSubmit } : {})}
      >
        {label}
      </Button>
    </div>
  );
}

export function FieldError({ message }: { message?: string | undefined }) {
  return message ? (
    <p role="alert" className="mt-2 text-xs text-danger">
      {message}
    </p>
  ) : null;
}

/// Agendado, confirmado pela empresa (ADR 0036), concluído por quem atendeu (ADR 0030) ou cancelado.
export function AppointmentStatusPill({
  status,
  requiresConfirmation,
}: {
  status: AppointmentStatus;
  requiresConfirmation?: boolean | undefined;
}) {
  if (status === 'SCHEDULED' && requiresConfirmation)
    return <StatusPill tone="warning">Solicitado · sem reserva</StatusPill>;
  if (status === 'NO_SHOW') return <StatusPill tone="warning">Faltou</StatusPill>;
  if (status === 'COMPLETED')
    return (
      <StatusPill tone="positive" icon={<Check className="size-3" />}>
        Concluído
      </StatusPill>
    );
  if (status === 'CONFIRMED') return <StatusPill tone="positive">Confirmado</StatusPill>;
  if (status === 'SCHEDULED') return <StatusPill tone="brand">Agendado</StatusPill>;
  return <StatusPill tone="neutral">Cancelado</StatusPill>;
}

export function AppointmentQueueNotice({
  appointment,
  timeZone,
}: {
  appointment: Appointment;
  timeZone: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const top = useRef<HTMLDivElement>(null);
  const position = appointment.queuePosition;
  const operationalTimeZone = appointment.timeZone ?? timeZone;
  const queue = useQuery({
    queryKey: [
      'company',
      'scheduling',
      'queue',
      appointment.id,
      appointment.startsAt,
      appointment.endsAt,
    ],
    queryFn: async () => {
      const [calendar, rooms] = await Promise.all([
        schedulingApi.getCalendar({
          businessUnitId: appointment.businessUnitId,
          from: civilDateOf(appointment.startsAt, operationalTimeZone),
          to: civilDateOf(appointment.endsAt, operationalTimeZone),
          includeCancelled: false,
        }),
        schedulingApi.listResources({
          businessUnitId: appointment.businessUnitId,
          activity: 'ALL',
          kind: 'ROOM',
          sort: 'displayName',
          direction: 'asc',
        }),
      ]);
      return { ...calendar, rooms };
    },
    enabled: open,
  });
  if (!position) return null;
  const normalize = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  const overlapping = (queue.data?.appointments ?? []).filter(
    (item) =>
      item.businessUnitId === appointment.businessUnitId &&
      item.startsAt < appointment.endsAt &&
      item.endsAt > appointment.startsAt,
  );
  const capacity = queue.data?.rooms.find((room) => room.id === appointment.roomId)?.capacity;
  const events = overlapping
    .filter(
      (item) =>
        item.roomId === appointment.roomId &&
        ['SCHEDULED', 'CONFIRMED', 'COMPLETED'].includes(item.status),
    )
    .flatMap((item) => [
      { at: Math.max(Date.parse(item.startsAt), Date.parse(appointment.startsAt)), delta: 1 },
      { at: Math.min(Date.parse(item.endsAt), Date.parse(appointment.endsAt)), delta: -1 },
    ])
    .sort((a, b) => a.at - b.at || a.delta - b.delta);
  let occupied = 0;
  const roomCompetition =
    capacity !== undefined &&
    events.some((event) => {
      occupied += event.delta;
      return occupied > capacity;
    });
  const entries = overlapping
    .filter(
      (item) =>
        item.businessUnitId === appointment.businessUnitId &&
        item.status === 'SCHEDULED' &&
        item.requiresConfirmation &&
        item.queuePosition &&
        item.startsAt < appointment.endsAt &&
        item.endsAt > appointment.startsAt &&
        (item.professionalId === appointment.professionalId ||
          item.anyProfessional ||
          appointment.anyProfessional ||
          (Boolean(item.roomId && appointment.roomId) && (item.anyRoom || appointment.anyRoom)) ||
          (roomCompetition && item.roomId === appointment.roomId)),
    )
    .sort(
      (a, b) =>
        a.queuePosition! - b.queuePosition! ||
        (a.requestedAt ?? a.createdAt).localeCompare(b.requestedAt ?? b.createdAt) ||
        a.id.localeCompare(b.id),
    );
  const visible = entries.filter((item) =>
    normalize(
      `${item.clientDisplayName} ${item.serviceDisplayName} ${item.professionalDisplayName} ${item.roomDisplayName ?? ''}`,
    ).includes(normalize(search)),
  );

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setSearch('');
          setOpen(true);
        }}
        aria-label={`Ver fila de ${appointment.clientDisplayName}`}
        className="mt-2 flex w-full items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-left text-warning focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning"
      >
        <UsersRound aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-bold">Cliente na fila</p>
          <p className="text-xs underline">Ver fila</p>
          <p className="text-xs font-semibold">Posição {position} na fila</p>
          <p className="mt-1 text-xs text-content">Aguardando confirmação · horário sem reserva</p>
        </div>
      </button>
      <SidePanel
        open={open}
        onOpenChange={setOpen}
        title="Fila de agendamentos"
        description="Solicitações que disputam os recursos deste horário. A posição não garante reserva; a confirmação revalida a disponibilidade."
        footer={
          <Button
            variant="outline"
            onClick={() => top.current?.scrollIntoView({ behavior: 'smooth' })}
          >
            Voltar ao topo
          </Button>
        }
      >
        <div ref={top} />
        <ListFilterBar
          variant="panel"
          searchLabel="Buscar na fila"
          search={search}
          onSearchChange={setSearch}
          count={`${visible.length} na fila`}
        />
        {queue.isLoading ? (
          <p role="status" className="mt-4">
            Carregando fila…
          </p>
        ) : queue.error ? (
          <p role="alert" className="mt-4 text-danger">
            {getApiError(queue.error)}
          </p>
        ) : (
          <>
            <p className="my-4 text-sm text-content-muted">
              {entries.length} solicitações · ordem da fila
            </p>
            <ol className="space-y-3" aria-label="Solicitações na fila">
              {visible.map((item) => (
                <li
                  key={item.id}
                  aria-current={item.id === appointment.id ? 'true' : undefined}
                  className={`rounded-xl border p-4 ${item.id === appointment.id ? 'border-brand-500 bg-brand-500/10 ring-2 ring-brand-500/30' : 'border-surface-border'}`}
                >
                  {item.id === appointment.id ? (
                    <p className="mb-2 text-xs font-bold text-brand-400">Agendamento selecionado</p>
                  ) : null}
                  <p className="text-sm font-semibold">
                    Posição {item.queuePosition} · {item.clientDisplayName}
                  </p>
                  <p className="mt-1 text-sm">{item.serviceDisplayName}</p>
                  <p className="mt-1 text-xs text-content-muted">
                    {formatDateTime(item.startsAt, item.timeZone ?? timeZone)} ·{' '}
                    {item.durationMinutes} min
                  </p>
                  <p className="mt-1 text-xs text-content-muted">
                    {item.professionalDisplayName} · {item.roomDisplayName ?? 'Sem sala'}
                  </p>
                </li>
              ))}
            </ol>
            {visible.length === 0 ? (
              <p className="mt-4 text-sm">
                Nenhuma solicitação encontrada. A fila pode ter sido atualizada.
              </p>
            ) : null}
          </>
        )}
      </SidePanel>
    </>
  );
}
